/**
 * Saved Accounts Page Component
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Plus, Zap, Droplets, Tv, CreditCard, MapPin, Trash2, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import { API_URL, BACKEND_URL } from '../config';


const SavedAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("auth_token");
      const [accountsRes, providersRes] = await Promise.all([
        axios.get(`${API_URL}/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/providers`)
      ]);
      setAccounts(accountsRes.data);
      setProviders(providersRes.data);
    } catch (err) {
      const classified = classifyError(err);
      setError(classified);
      toast.error(classified.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm("Delete this saved account?")) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API_URL}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(prev => prev.filter(a => a.id !== accountId));
      toast.success("Account deleted");
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to delete account");
    }
  };

  const getProviderIcon = (provider) => {
    if (!provider) return <CreditCard className="w-6 h-6 text-gray-500" />;
    if (provider.type === "electricity") return <Zap className="w-6 h-6 text-yellow-500" />;
    if (provider.type === "water") return <Droplets className="w-6 h-6 text-cyan-500" />;
    if (provider.type === "tv") return <Tv className="w-6 h-6 text-blue-500" />;
    return <CreditCard className="w-6 h-6 text-gray-500" />;
  };

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 loading-shimmer rounded" /></div>;

  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Meters you know by heart</h1>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-all" data-testid="add-account-btn">
          <Plus className="w-5 h-5" /> Add Account
        </button>
      </div>

      {accounts.length > 0 ? (
        <div className="grid gap-4">
          {accounts.map((account) => {
            const provider = providers.find(p => p.code === account.provider_code);
            return (
              <div key={account.id} className="flex items-center gap-4 p-4 bg-card border border-border/50 rounded-xl">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${provider?.type === "electricity" ? "bg-yellow-500/20" : provider?.type === "tv" ? "bg-blue-500/20" : "bg-cyan-500/20"}`}>
                  {getProviderIcon(provider)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{account.nickname || "Unnamed"}</p>
                  <p className="text-sm text-muted-foreground">{account.provider_code}</p>
                  <p className="text-sm font-mono text-muted-foreground">{account.account_number}</p>
                </div>
                {account.location && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-4 h-4" />{account.location.name}</p>}
                <button onClick={() => handleDelete(account.id)} className="p-2 hover:bg-red-500/10 hover:text-red-500 rounded-lg transition-all">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12"><Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" /><p className="text-muted-foreground mb-4">No saved accounts yet</p></div>
      )}

      <AnimatePresence>
        {showAddModal && (
          <AddAccountModal providers={providers} onClose={() => setShowAddModal(false)} onSuccess={(account) => { setAccounts(prev => [...prev, account]); setShowAddModal(false); }} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const AddAccountModal = ({ providers, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ provider_code: "", account_number: "", nickname: "", location: null });
  const [loading, setLoading] = useState(false);
  const selectedProvider = providers.find(p => p.code === formData.provider_code);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.provider_code || !formData.account_number) { toast.error("Please fill required fields"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.post(`${API_URL}/accounts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Account saved!");
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-card border border-border/50 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Add Account</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Provider</label>
            <select value={formData.provider_code} onChange={(e) => setFormData(prev => ({ ...prev, provider_code: e.target.value, location: null }))}
              className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all">
              <option value="">Select provider</option>
              {providers.map(p => <option key={p.code} value={p.code}>{p.name || p.code}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{selectedProvider?.account_label || "Meter Number"}</label>
            <input type="text" value={formData.account_number} onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))} placeholder="Enter number"
              className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Nickname (Optional)</label>
            <input type="text" value={formData.nickname} onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))} placeholder="e.g., Home, Office"
              className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
          </div>

          {selectedProvider?.locations?.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">Location (Optional)</label>
              <select value={formData.location?.code || ""} onChange={(e) => { const loc = selectedProvider.locations.find(l => l.code === e.target.value); setFormData(prev => ({ ...prev, location: loc || null })); }}
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary transition-all">
                <option value="">Select location</option>
                {selectedProvider.locations.map(loc => <option key={loc.code} value={loc.code}>{loc.name}</option>)}
              </select>
            </div>
          )}

          <button type="submit" disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Account"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default SavedAccountsPage;